import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconFileExport } from "@/lib/icons";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "@/lib/api/client";
import { type FormTemplate, generateForm } from "@/lib/api/verdict";

const TEMPLATES: FormTemplate[] = ["form_086", "form_083", "form_089", "custom"];

export function GenerateFormDialog({
  examId,
  opened,
  onClose,
}: {
  examId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const form = useForm({
    initialValues: { template: "form_086" as FormTemplate },
  });

  const mutation = useMutation({
    mutationFn: async (template: FormTemplate) => {
      const response = await generateForm(examId, template, i18n.language);
      const pdfResp = await api.get<Blob>(`/forms/${response.form.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(pdfResp.data as Blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return response;
    },
    onSuccess: (response) => {
      notifications.show({
        color: "green",
        title: t("verdict.form_generated"),
        message: response.form.form_number,
      });
      form.reset();
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal opened={opened} onClose={onClose} title={t("verdict.generate_title")} size="md">
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v.template))}>
        <Stack>
          <Select
            label={t("verdict.fields.template")}
            placeholder={t("placeholder.select")}
            data={TEMPLATES.map((tp) => ({
              value: tp,
              label: t(`examinations.types.${tp}`, { defaultValue: tp }),
            }))}
            {...form.getInputProps("template")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              leftSection={<IconFileExport size={16} />}
              loading={mutation.isPending}
              radius="xl"
            >
              {t("verdict.generate")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
